/*
 * Calendar connections, on the profile page. Google and Outlook, one row each.
 *
 * ⚠️ **THIS IS NOT THE CALENDAR FEED CARD WITH DIFFERENT BUTTONS, AND THE DIFFERENCE IS THE
 * WHOLE REASON IT EXISTS.** A feed is a link you paste into a calendar app, which then polls
 * it read-only on its own schedule. A connection is permission you grant once, after which
 * HolidayHive writes your leave into your calendar directly and removes it when leave is
 * cancelled.
 *
 * ⚠️ **AND IT IS THE ONLY ROUTE THAT REACHES SOME OUTLOOK USERS AT ALL.** Rob's own
 * Microsoft 365 account refuses internet calendar subscriptions outright — including
 * Timetastic's own live feed, which is the control test that proved the refusal is not our
 * code (19/08/2026). For a customer whose tenant is configured that way, the feed card can
 * never work and this one is the answer.
 *
 * ⚠️ **THERE IS NO APPLE ROW AND THERE NEVER WILL BE — ROB'S DECISION, 21/08/2026, SETTLED.**
 * Apple has no OAuth for calendar data. The only route is CalDAV with a 16-character
 * app-specific password the person generates by hand, which is an unscoped credential to
 * their whole account that we would have to store. Timetastic do not offer one either.
 * Apple Calendar subscribes to the `.ics` feed perfectly well; it is Outlook that refuses.
 *
 * ⚠️ Two pieces of copy in here are load-bearing, for both providers:
 *
 *  1. **The account address.** A person can be signed in to a personal account in the same
 *     browser and approve with that one without noticing, and their holidays would then be
 *     written, correctly, into a calendar they never look at. Showing which account we were
 *     actually given is the only way they would ever find out.
 *  2. **The partial-consent line.** Both providers let somebody approve the sign-in part and
 *     refuse the calendar part, and the flow still completes successfully. Saying
 *     "connected" and stopping there would be a confident wrong answer — the same mistake as
 *     "Not added to a calendar yet" on 20/08/2026.
 */
import React, { useState, useEffect } from 'react';
import { Loader2, Link2Off, ExternalLink, AlertTriangle, CalendarPlus } from 'lucide-react';
import { Button } from './ui/button';
import { calendarConnectionApi } from '../lib/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

/*
 * ⚠️ **THE ONLY PLACE A PROVIDER IS NAMED IN THIS FILE.** Everything below reads this table.
 * A `provider === 'google' ? ... : ...` anywhere in the rendering is how the two rows start
 * quietly diverging, and it would be the Google one that ended up wrong — Outlook is the one
 * everybody tests by hand.
 *
 * ⚠️ `where` is not decoration. Rob connected on staging, opened Outlook, compared his
 * calendar list before and after and found them identical (20/08/2026). Correctly — we write
 * into the calendar he already had, which is decision G, because only the default calendar
 * feeds free/busy. Saying where the events go is the difference between "it worked" and "did
 * anything happen?".
 */
const PROVIDERS = [
  {
    id: 'google',
    name: 'Google Calendar',
    where: 'your main Google calendar',
    admin: 'Google Workspace administrator',
  },
  {
    id: 'microsoft',
    name: 'Outlook',
    where: 'your main Outlook calendar',
    admin: 'IT administrator',
  },
];

/*
 * What the backend puts in `?calendar=` when it sends the browser back here.
 *
 * ⚠️ EVERY OUTCOME GETS ITS OWN SENTENCE, INCLUDING THE ONES THAT ARE NOT OUR FAULT. A
 * single "something went wrong" for all of them is what turns a tenant policy — which the
 * customer's own IT can fix in a minute — into a bug report about us.
 *
 * ⚠️ **EACH ONE CALLS `toast` WITH A PLAIN STRING LITERAL, ON ONE LINE, AND THAT SHAPE IS
 * REQUIRED RATHER THAN PREFERRED.** `frontend/scripts/gen_message_catalog.py` scans for
 * `toast.x('...')` and only catalogues literal first arguments — so a tidier version of
 * this, holding the text in a `{ kind, message }` object and calling `toast[kind](message)`
 * once, puts every one of these messages beyond the reach of Superadmin → Messages, where
 * they cannot be re-worded. That was the first version of this file, and the catalogue
 * silently picked up three of the eight toasts in it.
 *
 * ⚠️ **THE WORDING STOPPED NAMING THE PROVIDER WHEN THE SECOND ONE ARRIVED, AND THAT IS A
 * DELIBERATE TRADE.** Interpolating the name would make these template literals, which the
 * catalogue cannot see. The provider is named on the row the person is looking at, so the
 * toast does not have to carry it.
 */
const OUTCOMES = {
  connected: () => toast.success('Calendar connected.'),
  refused: () => toast.info('No changes made — connecting was cancelled.'),
  no_permission: () => toast.error('Connected, but calendar access wasn’t granted. Try again and approve the calendar permission, or ask your administrator to approve HolidayHive.'),
  expired: () => toast.error('That took too long, so the link expired. Please try connecting again.'),
  failed: () => toast.error('Couldn’t finish connecting. Please try again.'),
};

/*
 * ⚠️ **THE CARD FRAME LIVES IN HERE, NOT IN ProfilePage.js, AND THAT DIFFERENCE IS
 * DELIBERATE.** Every other card on that page — two-factor, calendar feeds — is wrapped by
 * the page, which is fine because they always have something to say. This one renders
 * nothing at all when neither provider has been configured, and a heading left behind out
 * there would give a brand-new installation an empty card titled "Calendar connections": a
 * feature that looks broken rather than one that is simply absent.
 */
const Card = ({ children }) => (
  <div className="card-hive p-6" data-testid="calendar-connections-card">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
        <CalendarPlus className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h3 className="font-heading font-bold">Calendar connections</h3>
        <p className="text-sm text-muted-foreground">
          Put your booked leave straight into your own calendar
        </p>
      </div>
    </div>
    <div className="divide-y divide-border">{children}</div>
  </div>
);

/*
 * ⚠️ **HOW LONG BEFORE "LAST CHECKED" IS WORTH WORRYING ABOUT.** The worker runs once a
 * minute, so fifteen is generous — long enough to survive a deploy, a slow tick or a
 * restart without crying wolf, short enough that a stopped worker is visible within the
 * quarter hour rather than the next day. ⚠️ The feed card uses 24 hours for the same idea
 * and that is not an inconsistency: nobody controls when Google polls a subscription,
 * whereas this loop is ours.
 */
const STALE_AFTER_MS = 15 * 60 * 1000;

const SyncLine = ({ status }) => {
  /*
   * ⚠️ **THREE STATES, THREE DIFFERENT ANSWERS, AND CONFLATING THEM MAKES THE LINE
   * USELESS.** Never checked is not the same as checked and quiet, which is not the same as
   * checked a long time ago.
   *
   * ⚠️ And the first one must not claim anything about the customer. "Nothing has synced
   * yet" would be a statement about their leave; what we actually know is that we have no
   * record of a check. On the feed card that exact slip told Rob he had never added a
   * subscription that was working perfectly (handover 3-quater).
   */
  if (!status.last_synced_at) {
    return (
      <p className="text-xs text-muted-foreground mt-2">
        Waiting for the first check &mdash; it runs every minute.
      </p>
    );
  }

  const when = new Date(status.last_synced_at);
  const stale = Date.now() - when.getTime() > STALE_AFTER_MS;
  const count = status.events_in_calendar;

  return (
    <p
      className={`text-xs mt-2 ${stale ? 'text-amber-600' : 'text-muted-foreground'}`}
      data-testid={`calendar-last-synced-${status.provider}`}
    >
      Last checked {formatDistanceToNow(when, { addSuffix: true })}
      {stale && ' — longer than usual'}
      {/*
        * ⚠️ Zero is a real and ordinary answer — somebody with no leave booked — so it must
        * read as "nothing to add", never as a failure. "0 holidays in your calendar" beside
        * a healthy connection looks exactly like something is broken.
        */}
      {' · '}
      {count === 0
        ? 'no leave to add yet'
        : `${count} ${count === 1 ? 'booking' : 'bookings'} in your calendar`}
    </p>
  );
};

const ProviderRow = ({ provider, status, onChanged }) => {
  const [starting, setStarting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const connect = async () => {
    setStarting(true);
    try {
      const { data } = await calendarConnectionApi.start(provider.id);
      /*
       * ⚠️ A REAL NAVIGATION, AND IT HAS TO BE. The provider has to redirect the browser
       * away and back; fetching this URL instead would fail with a CORS error that reads
       * like our bug. Same reasoning as the note in `SsoButtons.jsx` — the difference is
       * only that the URL is fetched with a token first, because this flow has to know who
       * is asking.
       */
      window.location.href = data.authorize_url;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Couldn’t start the connection. Please try again.');
      setStarting(false);
    }
    // ⚠️ No `finally`. On the happy path the browser is leaving, and clearing the busy
    // state would flick the button back to "Connect" for the half-second before it goes.
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      const { data } = await calendarConnectionApi.disconnect(provider.id);
      onChanged(data);
      setConfirming(false);
      toast.success('Disconnected. Anything already in your calendar has been left alone.');
    } catch (error) {
      toast.error('Couldn’t disconnect. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!status.connected) {
    return (
      <div className="py-4 first:pt-0 last:pb-0"
           data-testid={`calendar-connection-row-${provider.id}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-sm">{provider.name}</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              You&rsquo;ll be sent to {provider.name} to sign in and allow it. Only you can do
              this for your own calendar &mdash; nobody can connect it on your behalf.
            </p>
          </div>
          <Button
            onClick={connect}
            disabled={starting}
            className="btn-primary shrink-0"
            size="sm"
            data-testid={`connect-calendar-${provider.id}`}
          >
            {starting
              ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Working...</>)
              : (<><ExternalLink className="w-4 h-4 mr-1" />Connect</>)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4 first:pt-0 last:pb-0 space-y-4"
         data-testid={`calendar-connection-row-${provider.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium text-sm"
             data-testid={`calendar-account-${provider.id}`}>
            {/*
              * ⚠️ The address, not "Connected". Which account this landed on is the fact
              * worth showing — see the note at the top of this file.
              */}
            {provider.name} &mdash; connected as{' '}
            {status.account_email || `your ${provider.name} account`}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Approved leave only, in {provider.where}. Cancel a booking and it comes straight
            back out.
          </p>

          {/*
            * ⚠️ **THE LINE THAT ANSWERS "IS IT WORKING?" WITHOUT ASKING ANYBODY.** Decision
            * G-bis, from Rob's question *"how do we know when it has been built?"*. The feed
            * card learned this the hard way (handover 3-quater): three states, three
            * different answers, and never turning "we have no record" into "it has not
            * happened".
            */}
          <SyncLine status={status} />
        </div>

        {confirming ? null : (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => setConfirming(true)}
            data-testid={`disconnect-calendar-${provider.id}`}
          >
            <Link2Off className="w-4 h-4 mr-1" />
            Disconnect
          </Button>
        )}
      </div>

      {/*
        * ⚠️ **THE PROVIDER HAS WITHDRAWN ACCESS, WHICH IS A NORMAL EVENT AND NOT AN ERROR.**
        * A person can revoke us from their own account at any time, a password change can do
        * it, and an administrator can pull consent for a whole company overnight — none of
        * it notifies us. Without this the connection would simply stop working while the row
        * carried on saying "connected", which is the worst of both.
        *
        * ⚠️ Only set for failures about the CONNECTION. One holiday the provider refused
        * lives on the ledger row instead — asking somebody to redo their whole connection
        * over a single bad booking would be wrong.
        */}
      {status.needs_reconnect && (
        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 flex gap-2"
             data-testid={`calendar-needs-reconnect-${provider.id}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <p className="text-sm text-muted-foreground">
            {provider.name} has stopped letting HolidayHive update your calendar, so nothing
            is being added or removed at the moment. This usually means the permission was
            withdrawn or your password changed. Disconnect and connect again to put it back.
          </p>
        </div>
      )}

      {/*
        * ⚠️ THE HONEST HALF OF "CONNECTED". Both providers allow partial consent — approve
        * the sign-in, refuse the calendar — and the flow still succeeds. Without this the row
        * would say connected, confidently, about something that can never write anything.
        */}
      {!status.can_write_calendar && (
        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <p className="text-sm text-muted-foreground">
            {provider.name} didn&rsquo;t grant permission to change your calendar, so nothing
            can be added to it. Disconnect and connect again, approving the calendar
            permission &mdash; or ask your {provider.admin} to approve HolidayHive for your
            organisation.
          </p>
        </div>
      )}

      {/*
        * The confirm step is inline rather than a dialog, matching the reset flow on the
        * calendar feed card. ⚠️ It says what disconnecting does NOT do, because that is the
        * question somebody actually has at this moment.
        */}
      {confirming && (
        <div className="p-4 rounded-lg border border-border">
          <p className="text-sm">
            HolidayHive will stop adding and removing your leave. Anything already in your
            calendar stays where it is &mdash; nothing is removed.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(false)}
              disabled={disconnecting}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={disconnect}
              disabled={disconnecting}
              data-testid={`confirm-disconnect-calendar-${provider.id}`}
            >
              {disconnecting
                ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Working...</>)
                : 'Yes, disconnect'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const CalendarConnectionsCard = () => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await calendarConnectionApi.list();
        setConnections(data.connections || []);
      } catch (error) {
        // ⚠️ Say it failed rather than rendering an empty card — an empty card reads as
        // "not connected", which is a different and possibly wrong answer.
        setFailed(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /*
   * The return leg. The provider sends the browser to /profile?calendar=<outcome>, so this
   * reports what happened and then removes the parameter.
   *
   * ⚠️ **THE URL IS CLEANED FOR A REASON, NOT FOR TIDINESS.** Left in place, the message
   * reappears on every refresh and on every back-navigation for the rest of the session —
   * so somebody who disconnects would be told "Calendar connected" the next time they
   * reloaded the page.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const announce = OUTCOMES[params.get('calendar')];
    if (!announce) return;

    announce();

    params.delete('calendar');
    params.delete('provider');
    const query = params.toString();
    window.history.replaceState(
      {}, '', window.location.pathname + (query ? `?${query}` : ''));
  }, []);

  const replace = (updated) => setConnections((rows) =>
    rows.map((row) => (row.provider === updated.provider ? updated : row)));

  /*
   * ⚠️ NOTHING WHILE LOADING, not a spinner in a frame. This card may turn out not to
   * exist at all, and a titled card that appears for half a second and then vanishes is a
   * worse flicker than one that arrives a moment late.
   */
  if (loading) return null;

  if (failed) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">
          Couldn&rsquo;t load your calendar connections. Please refresh the page.
        </p>
      </Card>
    );
  }

  /*
   * ⚠️ Only the providers this installation has keys for, and nothing at all when there are
   * none — rather than buttons that cannot work. `SsoButtons.jsx` does the same thing for
   * the same reason: offering a control that fails on every press is worse than not
   * offering it, and a customer cannot tell "not configured" from "broken".
   *
   * ⚠️ **THE ORDER COMES FROM `PROVIDERS`, NOT FROM THE API RESPONSE.** A row order that
   * depended on what the backend happened to return would reshuffle the moment somebody
   * added keys for the other provider.
   */
  const rows = PROVIDERS
    .map((provider) => ({
      provider,
      status: connections.find((row) => row.provider === provider.id),
    }))
    .filter(({ status }) => status?.available);

  if (!rows.length) return null;

  return (
    <Card>
      {rows.map(({ provider, status }) => (
        <ProviderRow
          key={provider.id}
          provider={provider}
          status={status}
          onChanged={replace}
        />
      ))}
    </Card>
  );
};

export default CalendarConnectionsCard;
